import React, { useEffect, useState } from "react";
import { Col, Form, Input, Row, message } from "antd";
import VitalService from "../../../../Services/VitalService";
import { convertUTCDate } from "../../../../utils/convertionFunc";

const AddVitals = (props) => {
  const [vitalMasterData, setVitalMasterData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    "BPS": "",
    "BPD": "",
    "SPO2": "",
    "HR": "",
    "RR": "",
    "TP": "",
    "HE": "",
    "WE": "",
    "BMI": "",
  });

  // alias mapping: form field => vital master name
  const aliasMap = {
    "Pulse Ox": "Pulse Oxygen",
    "Height": "Height",
    "Weight": "Weight",
    "BMI": "BMI",
    "Temperature": "Temperature",
    "Heart Rate": "Heart Rate",
    "Respiration Rate": "Respiration Rate",
    "Blood Pressure(SYS)": "Blood Pressure(SYS)",
    "Blood Pressure(DIA)": "Blood Pressure(DIA)",
    "Blood Glucose":"Blood Glucose"
  };

  useEffect(() => {
    VitalService.VitalMasterlist({ status: 1 }).then((res) => {
      setVitalMasterData(res.data || []);
    });
  }, []);

  const handleInputChange = (value, fieldName) => {
    const updatedFormData = { ...formData, [fieldName]: value };

    if ((fieldName === "HI" || fieldName === "WE")) {
      const height = updatedFormData["HI"];
      const weight = updatedFormData["WE"];
      const h = parseFloat(height);
      const w = parseFloat(weight);

      if (h > 0 && w > 0) {
        const bmi = (w / ((h / 100) * (h / 100))).toFixed(1);
        updatedFormData.BMI = bmi;
      } else {
        updatedFormData.BMI = "";
      }
    }

    setFormData(updatedFormData);
  };

  const handleVitalSubmit = () => {
    const filledFields = Object.entries(formData).filter(([_, val]) => val !== "" && val !== null);
    if (filledFields.length === 0) {
      message.warning("Please fill in at least one field before submitting.");
      return;
    }

    const userObj = sessionStorage.getItem("login_result");
    const userData = userObj ? JSON.parse(userObj) : null;

    const payload = filledFields.map(([vitalCode, value]) => {
      // const masterName = aliasMap[fieldName] || fieldName;
      const matchedVital = vitalMasterData.find((vital) => vital.vitalCode === vitalCode);

      if (!matchedVital) return null;

      return {
        account_Id: props.selectedPatient?.account_Id,
        clinic_Id: props.selectedPatient?.clinic_Id,
        consultationId: null,
        createdBy: userData?.user?.userId,
        createdDate: convertUTCDate(new Date(), "YYYY-MM-DD HH:mm:ss"),
        modifiedBy: userData?.user?.userId,
        modifiedDate: convertUTCDate(new Date(), "YYYY-MM-DD HH:mm:ss"),
        patientId: props.selectedPatient?.patientId,
        recordedDateTime: convertUTCDate(new Date(), "YYYY-MM-DD HH:mm:ss"),
        status: 1,
        units: matchedVital.units,
        value: value,
        vitalCode: matchedVital.vitalCode,
        vitalName: matchedVital.vitalName,
        ...( props.selectedPatient?.rpmApplicable === 'Y' && {rpmApplicable: 'Y'})
      };
    }).filter(Boolean);

    if (payload.length === 0) {
      message.error("No valid vital fields matched master data.");
      return;
    }

    setIsLoading(true);
    VitalService.AddVitals(payload)
      .then((response) => {
        props.updatedVitalsList?.();
        message.success("Vitals added successfully.");
        props.onClose?.();
      })
      .catch((err) => {
        console.error("Vital submission failed", err);
        message.error("Something went wrong. Please try again.");
      })
      .finally(() => setIsLoading(false));
  };

  return (
    <div className="mt-6 border rounded-xl p-1.5">
      <Form layout="vertical" disabled={isLoading}>
        <Row gutter={24}>
          <Col span={8}>
            <Form.Item label="Blood Pressure" className="mx-2">
              <div className="flex items-center border rounded-lg border-brand-textGray h-10">
                <Input
                  onChange={(e) => handleInputChange(e.target.value, "BPS")}
                  maxLength={3}
                  placeholder="SYS"
                  className="border-none w-1/2 text-end"
                />
                <span className="w-1/6 text-center">/</span>
                <Input
                  onChange={(e) => handleInputChange(e.target.value, "BPD")}
                  maxLength={3}
                  placeholder="DIA"
                  className="border-none w-1/2"
                  suffix={<span style={{ color: "#BEC0C3" }}>mmHg</span>}
                />
              </div>
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="Pulse Ox" className="mx-2">
              <Input
                className="h-10"
                suffix="%"
                onChange={(e) => handleInputChange(e.target.value, "SPO2")}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="Heart Rate" className="mx-2">
              <Input
                className="h-10"
                suffix="Beats/min"
                onChange={(e) => handleInputChange(e.target.value, "HR")}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col span={6}>
            <Form.Item label="Respiration Rate" className="mx-2">
              <Input
                className="h-10"
                suffix="Breaths/min"
                onChange={(e) => handleInputChange(e.target.value, "RR")}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="Temperature" className="mx-2">
              <Input
                className="h-10"
                suffix="°F"
                onChange={(e) => handleInputChange(e.target.value, "TP")}
              />
            </Form.Item>
          </Col>
          {/* <Col span={6}>
            <Form.Item label="Blood Glucose" className="mx-2">
              <Input
                className="h-10"
                suffix="mg/dL"
                onChange={(e) => handleInputChange(e.target.value, "BG")}
              />
            </Form.Item>
          </Col> */}
        </Row>
      </Form>

      <hr className="my-4 border-gray-400" />

      <Form layout="vertical">
        <Row gutter={24}>
          <Col span={6}>
            <Form.Item label="Height" className="mx-2">
              <Input
                className="h-10"
                suffix="Cm"
                onChange={(e) => handleInputChange(e.target.value, "HI")}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="Weight" className="mx-2">
              <Input
                className="h-10"
                suffix="Kg"
                onChange={(e) => handleInputChange(e.target.value, "WE")}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="BMI" className="mx-2">
              <Input
                className="h-10"
                disabled
                suffix="Kg/m²"
                value={formData.BMI}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={24} className="flex justify-end gap-4 my-5">
            <button
             disabled={isLoading}
              className="md:min-w-32 w-[6rem] p-2 border rounded-lg text-brand-primaryColor border-brand-primaryColor font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={props.handleClosemodal}
            >
              Cancel
            </button>
            <button
              disabled={isLoading}
              className="md:min-w-32 w-[6rem] p-2 bg-brand-primaryColor text-white rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleVitalSubmit}
            >
              Save
            </button>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default AddVitals;

